import React, {
  CSSProperties, MutableRefObject, ReactElement, useEffect, useRef,
} from 'react';
import * as echarts from 'echarts';
import { EChartsOption, EChartsType } from 'echarts';
import * as Moment from 'moment-timezone';
import { useCurrentWidth } from 'react-socks';
import { ECElementEvent } from 'echarts/types/src/util/types';
import { HeatmapDataItemOption } from 'echarts/types/src/chart/heatmap/HeatmapSeries';
import { ChartType } from '../../../types/ChartType';
import { Direction } from '../../../types/Direction';
import { Movement } from '../../../types/Movement';
import { AggDataElement } from '../../../types/AggDataElement';
import { totallyDarkeChartsTheme } from '../../../util/theme';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { generateListOfTimes } from '../../../util/tstamp';
import { setPmState } from '../../../store/entities/pm';
import useDebouncedEffect from '../../../util/UseDebouncedEffect';
import { HeatmapPPDLauncher, HeatmapPPDLauncherHandle } from './HeatmapPPDLauncher';
import {
  CHART_GRID_BOTTOM_MARGIN,
  CHART_GRID_TOP_MARGIN, ECHARTS_LOADING_OPTION,
  LEVELS_OF_SERVICE,
  TIME_FORMAT,
  TITLE_MAP,
  USE_DARK_MODE,
} from '../../../util/constants';
import generateGuid from '../../../util/guid';
import { calculateFoldedMetrics, getIntersectionName } from '../../../util/util';
import { IntersectionId } from '../../../types/primitives';

type HeatmapBaseProps = {
  type: ChartType;
  direction: Direction;
  selectedChart: ChartType;
  selectedMovement: Movement;
  data: AggDataElement[];
} & typeof defaultProps;

const defaultProps = {
  style: {} as CSSProperties,
};

const SHOW_TOOLTIP = true;

// const getLevelOfService = (percent: number) => {
//   for (const level in LEVELS_OF_SERVICE) {
//     if (percent > (LEVELS_OF_SERVICE[level].min || 0)
//       && percent <= (LEVELS_OF_SERVICE[level].max || 100)) {
//       return level;
//     }
//   }
// };

interface IntersectionDataIndexMapItem {
  intersectionId: IntersectionId;
  min: number;
  max: number;
}

const HeatmapBase = ({
  selectedChart,
  type,
  selectedMovement,
  data,
  direction,
  style,
}: HeatmapBaseProps): ReactElement => {
  const dispatch = useAppDispatch();

  const availableIntersections = useAppSelector((state) => (
    state.entities.pm.availableIntersections));
  const selectedIntersections = useAppSelector((state) => state.entities.pm.selectedIntersections);
  const threshold = useAppSelector((state) => state.entities.pm.threshold);
  const isDataLoading = useAppSelector((state) => state.entities.pm.isDataLoading);
  const labelFontSize = useAppSelector((state) => state.entities.pm.labelFontSize);
  const timezoneMap = useAppSelector((state) => state.entities.pm.timezoneMap);
  const selectedIntersectionOnChart = useAppSelector((state) => (
    state.entities.pm.selectedIntersectionOnChart));

  const chartHeightMode = useAppSelector((state) => state.entities.pm.chartHeightMode);
  const customChartHeight = useAppSelector((state) => (
    state.entities.pm.customChartHeight));
  const customYAxisGap = useAppSelector((state) => (
    state.entities.pm.customYAxisGap));

  const heatmapPPDLauncherRef = useRef<HeatmapPPDLauncherHandle>() as
    MutableRefObject<HeatmapPPDLauncherHandle>;
  const chart = useRef<EChartsType>();
  const selectedIntersectionsRef = useRef<IntersectionId[]>(selectedIntersections);
  // This is to keep track of where each intersection starts and ends in the series data.
  const intersectionDataIndexMap = useRef<IntersectionDataIndexMapItem[]>();
  const id = useRef(generateGuid());

  const width = useCurrentWidth();

  const display = (selectedChart === type) ? null : 'none';

  useDebouncedEffect(() => {
    selectedIntersectionsRef.current = selectedIntersections;
  }, [selectedIntersections], 100);

  // When the selected intersection changes, we need to unselect everything on other charts.
  useEffect(() => {
    if (!selectedIntersectionOnChart) return;
    // If the chart exists and the one that changed isn't this chart
    if (chart.current && selectedIntersectionOnChart.chartId !== id.current) {
      chart.current?.dispatchAction({
        type: 'unselect',
        seriesIndex: 0,
        // Unselect every conceivable element (96 being the number of 15 minute bins in 24 hours)
        dataIndex: Array.from(Array(availableIntersections.length * 96)
          .keys()),
      });
    }
  }, [selectedIntersectionOnChart]);

  useEffect(() => {
    // TODO Change title
    // d3.select(`#heatmap-g-title-${type}-${direction}`)
    //     .text(`${title[type]}`)
  }, [selectedMovement]);

  useEffect(() => {
    if (chart.current) {
      if (isDataLoading) {
        chart.current?.showLoading('default', ECHARTS_LOADING_OPTION);
      } else {
        chart.current?.hideLoading();
      }
    }
  }, [isDataLoading]);

  useDebouncedEffect(() => chart.current && chart.current?.resize(), [width], 50);

  useEffect(() => {
    const opt = getOption();

    if (!chart.current) {
      echarts.registerTheme('totally-dark-theme', totallyDarkeChartsTheme);
      chart.current = echarts.init(document.getElementById(id.current) as HTMLElement,
        (USE_DARK_MODE) ? 'totally-dark-theme' : 'light',
        {
          renderer: 'canvas',
          locale: 'EN',
        });
      // @ts-ignore
      chart.current?.on('dblclick', onDoubleClickListener);
      // @ts-ignore
      chart.current?.on('select', onSelectListener);
      chart.current?.setOption(opt, { notMerge: true });
    }
    chart.current?.setOption(opt, { notMerge: true });
    chart.current?.resize();
  }, [data, customYAxisGap, customChartHeight, chartHeightMode, labelFontSize, threshold]);
  // The height of each intersection when dynamic chart height is enabled
  // I played around for a while in a graphing calculator to get this to feel right.
  // Try plotting y = A + B/x
  const A = 15;
  const B = 200;
  const MINIMUM_CHART_HEIGHT = 20;
  const getChartHeight = (numSelected: number) => {
    const dynamicTarget = A + (B / numSelected);

    switch (chartHeightMode) {
      case 'constant':
        return 300;
      case 'dynamic':
        return (dynamicTarget < MINIMUM_CHART_HEIGHT ? MINIMUM_CHART_HEIGHT : dynamicTarget)
          * numSelected;
      case 'custom':
        return customChartHeight;
      default:
        return 50;
    }
  };

  const onSelectListener = (params: ECElementEvent) => {
    if (!intersectionDataIndexMap.current) return;
    const dataIndex = params.dataIndexInside;
    const intersectionDataIndexMapItem = intersectionDataIndexMap.current.find((val) => (
      val.min <= dataIndex && dataIndex <= val.max));
    if (!intersectionDataIndexMapItem) return;
    dispatch(setPmState({
      selectedIntersectionOnChart: {
        intersectionId: intersectionDataIndexMapItem.intersectionId,
        chartId: id.current,
      },
    }));
  };

  const onMouseOutListener = () => {
    dispatch(setPmState({ currentMouseOverIntersection: null }));
  };

  const onDoubleClickListener = (params: ECElementEvent) => {
    const intersectionId = selectedIntersectionsRef.current[(params.value as number[])[1]];
    const time = generateListOfTimes()[(params.value as number[])[0]];
    if (heatmapPPDLauncherRef.current) {
      heatmapPPDLauncherRef.current.open(intersectionId, time, direction, selectedMovement);
    }
  };

  const getOption = (): EChartsOption => {
    const title = `${TITLE_MAP[type]} (${direction})`;

    // [00:00, 00:15, 00:30, ...]
    const times = generateListOfTimes();

    const foldOn = (d: AggDataElement) => Moment.unix(d.timestamp).tz(timezoneMap[d.intersectionId])
      .format(TIME_FORMAT);

    const folded = calculateFoldedMetrics(data, foldOn, times, type, threshold);

    // Make the array of data for the series, then sort it.
    const seriesData = (selectedIntersections.length ? folded.map((f) => ({
      value: [
        times.indexOf(f.category),
        selectedIntersections.indexOf(f.intersectionId),
        (selectedChart === 'los')
          ? Math.round(f.metric * 10) / 10 : Math.round(f.metric * 1000) / 10,
      ],
      name: `${f.category} - ${getIntersectionName(availableIntersections, f.intersectionId)}`,
    } as HeatmapDataItemOption
    )) : [])
      .sort((a, b) => {
        // Sort by intersection index
        if (a.value[1] > b.value[1]) return -1;
        if (a.value[1] < b.value[1]) return 1;

        // Sort by time
        if (a.value[0] > b.value[0]) return -1;
        if (a.value[0] < b.value[0]) return 1;
        return 0;
      });

    if (seriesData.length) {
      // Inspired by https://stackoverflow.com/a/55049764
      const tempIntersectionDataIndexMap: IntersectionDataIndexMapItem[] = seriesData.reduce(
        (acc, val, idx, arr) => {
          if (!idx || arr[idx - 1].value[1] !== val.value[1]) {
            if (acc.length) acc[acc.length - 1].max = idx - 1;
            // acc.find();
            // Add the new one
            acc.push({
              intersectionId: selectedIntersections[val.value[1] as number],
              min: idx,
              max: Number.MAX_SAFE_INTEGER,
            });
            // r.indices.push(i);
            // r.counts.push(1);
          }
          return acc;
        }, [] as IntersectionDataIndexMapItem[],
      );
      tempIntersectionDataIndexMap[tempIntersectionDataIndexMap.length - 1]
        .max = seriesData.length - 1;

      intersectionDataIndexMap.current = tempIntersectionDataIndexMap;
    }

    const len = selectedIntersections.length || 1;
    const chartHeight = getChartHeight(len);
    const shouldVisualMapBeHorizontal = chartHeight <= 175;

    return {
      grid: {
        top: `${CHART_GRID_TOP_MARGIN}`,
        bottom: `${CHART_GRID_BOTTOM_MARGIN}`,
        left: (chartHeightMode === 'custom') ? customYAxisGap : '250',
        right: '10',
        show: true,
      },
      toolbox: {
        right: 30,
        feature: {
          saveAsImage: {
            name: `pm_heatmap_${type}_${direction.toLowerCase()}_${selectedMovement.toLowerCase()}`,
            pixelRatio: 4,
            excludeComponents: ['toolbox', 'visualMap'],
          },
        },
      },
      title: { text: title },
      xAxis: {
        type: 'category',
        data: times,
        axisLine: {
          show: true,
          lineStyle: { color: '#999' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#999' },
        },
        axisLabel: {
          interval: 3,
          formatter: (value: string) => value.split(':')[0],
          color: USE_DARK_MODE ? '#fff' : '#000',
          fontSize: labelFontSize,
          hideOverlap: true,
          align: 'center',
        },
        name: 'Hour of Day',
        nameGap: 25,
        nameLocation: 'middle',
        nameTextStyle: {
          color: USE_DARK_MODE ? '#fff' : '#000',
          fontSize: labelFontSize,
        },
      },
      yAxis: {
        type: 'category',
        data: selectedIntersections.map((intId, idx) => idx),
        axisLine: {
          show: true,
          lineStyle: { color: '#999' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#999' },
        },
        axisLabel: {
          // show: selectedIntersections.length < 15,
          show: true,
          // d is "intersection_id number null"
          formatter: (value: string) => {
            // This is magic and somehow works. DON'T TOUCH IT!
            // eslint-disable-next-line no-control-regex
            const intersectionIndexStr = value.replace(/\u0000/g, '');
            const intersectionIndex = parseInt(intersectionIndexStr);
            const intersectionId = selectedIntersections[intersectionIndex];
            const intersections = availableIntersections.filter((i) => (
              i.intersectionId === intersectionId));
            if (intersections.length) return intersections[0].intersectionName;
            return `${intersectionId}`;
          },
          color: USE_DARK_MODE ? '#fff' : '#000',
          fontSize: labelFontSize,
        },
      },
      series: {
        type: 'heatmap',
        data: seriesData,
        select: {
          itemStyle: {
            borderWidth: 2,
            borderColor: '#000',
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, .5)',
          },
          label: {
            show: true,
            shadowColor: 'rgba(0, 0, 0, .5)',
            shadowBlur: 10,
            fontWeight: 'bolder',
            fontSize: labelFontSize + 5,
            valueAnimation: false,
            formatter: (params: { value: number[] }) => (type === 'los'
              ? `${(params.value[2]).toFixed(1)}s` : `${(params.value[2]).toFixed(1)}%`),
          },
        },
        label: {
          show: false,
          formatter: (params: { value: number[] }) => (type === 'los'
            ? `${(params.value[2]).toFixed(1)}s` : `${(params.value[2]).toFixed(1)}%`),
        },
        selectedMode: 'single',
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 2,
            borderColor: '#000',
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        animation: true,
        progressive: false,
        zlevel: -1,
      },
      // @ts-ignore
      tooltip: {
        position: 'top',
        borderWidth: 2,
        showDelay: 0,
        hideDelay: 0,
        show: SHOW_TOOLTIP,
        // formatter: (params: {name: string, value: number[]}) => (
        // formatter: params => params.value.join(':')
        formatter: (params: { name: string, value: number[] }): string => {
          const intersectionId = selectedIntersections[params.value[1]];
          dispatch(setPmState({ currentMouseOverIntersection: intersectionId }));
          const intersections = availableIntersections.filter((i) => (
            i.intersectionId === intersectionId));
          if (!intersections.length || !SHOW_TOOLTIP) return '';

          if (type === 'los') {
            return `<b>${(params.value[2]).toFixed(1)}s</b> - ${params.name}`;
          }

          return `<b>${(params.value[2]).toFixed(1)}%</b> - ${params.name}`;

          // return `
          //   <b>${intersections[0].intersectionName}</b>
          //   </br>🕒 ${time}</br>
          //   ${TITLE_MAP[type]}: ${(params.data[2] * 100).toFixed(1)}%`;
          // return (
          //     params.value[2] +
          //     ' commits in ' +
          //     hours[params.value[0]] +
          //     ' of ' +
          //     days[params.value[1]]
          // );
        },
      },
      ...(type === 'los' && {
        visualMap: {
          type: 'piecewise',
          // pieces: visualMapPieces
          pieces: Object.keys(LEVELS_OF_SERVICE)
            .map((l) => ({
              ...LEVELS_OF_SERVICE[l],
              label: l,
              textStyle: {
                fontSize: labelFontSize,
              },
            })),
          // inRange: Object.keys(LEVELS_OF_SERVICE).map(k => LEVELS_OF_SERVICE[k].color)
          orient: shouldVisualMapBeHorizontal ? 'horizontal' : 'vertical',
        },
      }),
      ...(type !== 'los' && {
        visualMap: {
          type: 'continuous',
          text: ['100%', '0%'],
          textGap: 5,
          itemHeight: 110,
          min: 0,
          max: 100,
          ...(['sf', 'dsb'].includes(type)
            && {
              range: [0, 25],
              outOfRange: {
                color: '#EA00EA',
              },
            }
          ),
          inRange: {
            ...(['sf', 'dsb'].includes(type)
              && { color: ['#CCFFCC', '#FFFF00', '#FF0000', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA'] }),
            ...(type === 'aog'
              && { color: ['#FF0000', '#FFFF00', '#CCFFCC'] }),
          },
          formatter: (value: number) => `${(value).toFixed(1)}%`,
          calculable: false,
          orient: shouldVisualMapBeHorizontal ? 'horizontal' : 'vertical',
          ...(shouldVisualMapBeHorizontal && { itemHeight: '50' }),
          // align: shouldVisualMapBeHorizontal ? 'right' : 'bottom',
          textStyle: {
            fontSize: labelFontSize,
          },
        },
      }),
      //     // @ts-ignore
      // visualMap: {
      //     type: chartType === "los" ? 'piecewise' : 'continuous',
      //     min: 0,
      //     max: Math.max(...seriesData.map(d => getValue(d[2]), chartType)),
      // }
    } as EChartsOption;
  };

  const len = selectedIntersections.length || 1;
  const chartHeight = getChartHeight(len);

  return (
    <>
      <div
        id={id.current}
        style={{
          ...style,
          ...(display === 'none' && { display }),
          height: (chartHeightMode === 'custom') ? `${chartHeight > 100 ? chartHeight : 100}px` : chartHeight,
          marginTop: '1em',
        }}
        // @ts-ignore
        onMouseLeave={onMouseOutListener}
      />
      <HeatmapPPDLauncher ref={heatmapPPDLauncherRef} />
    </>
  );
};

// const shuffle = (array: unknown[]): unknown[] => {
//   let currentIndex = array.length; let
//     randomIndex;
//
//   // While there remain elements to shuffle...
//   while (currentIndex !== 0) {
//     // Pick a remaining element...
//     randomIndex = Math.floor(Math.random() * currentIndex);
//     currentIndex--;
//
//     // And swap it with the current element.
//     // eslint-disable-next-line no-param-reassign
//     [array[currentIndex], array[randomIndex]] = [
//       array[randomIndex], array[currentIndex]];
//   }
//
//   return array;
// };

export default HeatmapBase;
